import { useEffect, useState, type JSX, type ReactNode } from 'react';
import { motion } from 'framer-motion';
import { AvatarFrame, MysteryFrame, type SeatPlayer } from './AvatarFrame.js';
import { prefersReducedMotion } from '../../preferences/reducedMotion.js';
import './MysteryFlipGrid.css';

/* Cadence for the V/F mystery-grid shuffle in ghost-meeting. One swap of two
   randomly-chosen cells per tick; Framer Motion's `layout` consumes the
   position delta via FLIP. */
const SHUFFLE_INTERVAL_MS = 1800;

type Tone = 0 | 1 | 2 | 3 | 4 | 5;

/* Tone follows the cell or player index, modulo 6. Identical in both modes
   so the colored band reads as a consistent six-tone cycle. */
function toneAt(index: number): Tone {
  return (index % 6) as Tone;
}

function buildInitialOrder(count: number): string[] {
  return Array.from({ length: count }, (_, i) => `cell-${i}`);
}

interface ShufflingProps {
  /**
   * `shuffling` runs the 1.8s swap loop; `settling` halts new swaps so the
   * in-flight Framer Motion `layout` transition can ease to a rest before
   * the phase boundary fires. Provides the wind-down beat without needing a
   * separate animation primitive — the existing FLIP transition's 900ms
   * ease-out tail is the deceleration.
   */
  mode: 'shuffling' | 'settling';
  /**
   * Cell count. Cells are seeded `cell-0` … `cell-{count-1}`; tone follows
   * the cell index so colors travel with each cell through the swap.
   */
  count: number;
  ariaLabel: string;
  /**
   * Inherit available vertical space inside a flex-column parent, packing
   * cells at the top via `align-content: start`. Ghost-meeting passes `true`
   * so the grid fills the verso below the prompt. Default `false`.
   */
  fill?: boolean;
}

interface RevealingProps {
  mode: 'revealing';
  /**
   * Players in canonical render order — the view layer sorts by `turnOrder`
   * upstream. Tone follows the array index (same `index % 6` cycle as
   * `shuffling` mode, so the mystery-side colour scheme is structurally
   * identical to ghost-meeting's last visible state at array-index level).
   */
  players: readonly SeatPlayer[];
  /**
   * Ids of players who are away (disconnected-but-alive). The back-face
   * AvatarFrame renders the away treatment for these. `SeatPlayer` doesn't
   * carry `connected`, so the set is threaded from the phase view rather than
   * read off the player. Default: nobody away.
   */
  awayPlayerIds?: ReadonlySet<string>;
  /**
   * When `true`, every cell's `data-cell-revealed` flips to `yes`; CSS
   * `transition-delay` staggers the cascade. Use `revealedCount` for an
   * explicit per-cell override (e.g. the mid-cascade mock).
   */
  revealed: boolean;
  /**
   * Cell that carries the active-speaker affordance (and aria-label) once
   * `revealed`. The lift is suppressed pre-reveal so it doesn't ghost-print
   * through the flip; CluegivingPhase.css drives the timing.
   */
  activePlayerId: string | null;
  /**
   * Returns the aria-label for the active speaker cell, e.g.
   * `Maya, writing the next clue`. Caller owns translation.
   */
  activeSpeakerLabel?: (player: SeatPlayer) => string;
  ariaLabel: string;
  /**
   * Rendered inside each cell after the flip card. Used by clue-giving for
   * the `ClueSlip`; the cell is `display: flex; flex-direction: column`
   * (CastGrid.css owns `.susdle-cluegiving-cast-cell` rules) so the footer
   * stacks under the card with a small gap.
   */
  renderCellFooter?: (player: SeatPlayer, index: number) => ReactNode;
  /**
   * Mock-state escape hatch: when set, the first N cells render as revealed
   * regardless of `revealed`, the rest as mystery. Disables the cascade
   * `data-cascade='no'` so no transition-delay applies and the state is
   * static. Used by `entry-cascade-midflight` to eyeball alignment during
   * a half-revealed moment.
   */
  revealedCount?: number;
  fill?: boolean;
}

type MysteryFlipGridProps = ShufflingProps | RevealingProps;

/**
 * Shared V/F grid component for ghost-meeting's mystery shuffle and
 * clue-giving's flip-reveal. One component, one CSS file, one set of
 * sizing tokens — cells land at byte-identical positions across the phase
 * boundary because the structure is literally the same.
 *
 * Three modes:
 *
 *   - `shuffling` (ghost-meeting V/F): six-tone mystery tiles, 1.8s
 *     swap loop, no back face.
 *   - `settling` (ghost-meeting V/F, just before handoff): halts new swaps;
 *     the in-flight FLIP transition's ease-out tail serves as the wind-down.
 *   - `revealing` (clue-giving V/F): same six-tone tiles on front face,
 *     AvatarFrame on back, 3D rotation cascade. Per-cell `data-cell-revealed`
 *     drives rotation so `revealedCount` can freeze the cascade for mock
 *     introspection.
 *
 * Ghost POV in clue-giving does not use this — Ghosts voted on the playing
 * order so there's no reveal cascade for them. They get `CastGrid` with a
 * quiet fade-in.
 */
export function MysteryFlipGrid(props: MysteryFlipGridProps): JSX.Element {
  if (props.mode === 'revealing') return <RevealingGrid {...props} />;
  return <ShufflingGrid {...props} />;
}

function ShufflingGrid({ mode, count, ariaLabel, fill }: ShufflingProps): JSX.Element {
  const [order, setOrder] = useState<string[]>(() => buildInitialOrder(count));

  /* Re-seed if the cell count changes. Doesn't happen in production
     (players-alive count is stable across a single meeting) but mock
     cycling between states with different counts needs the reset. */
  useEffect(() => {
    setOrder(buildInitialOrder(count));
  }, [count]);

  useEffect(() => {
    /* In `settling` mode we stop scheduling new swaps. Any FLIP transition
       that's mid-flight from the last shuffling-mode tick keeps running to
       its natural ease-out tail (Framer Motion `layout` does not cancel on
       interval clear; the `<motion.li>`'s active transition completes on
       its own timeline). That's the wind-down beat. */
    if (mode !== 'shuffling') return undefined;
    const reducedMotion = prefersReducedMotion();
    if (reducedMotion || count < 2) return undefined;

    const interval = window.setInterval(() => {
      setOrder((current) => {
        if (current.length < 2) return current;
        const a = Math.floor(Math.random() * current.length);
        let b = Math.floor(Math.random() * current.length);
        while (b === a) b = Math.floor(Math.random() * current.length);
        const next = [...current];
        [next[a], next[b]] = [next[b]!, next[a]!];
        return next;
      });
    }, SHUFFLE_INTERVAL_MS);

    return () => window.clearInterval(interval);
  }, [count, mode]);

  return (
    <ul
      className="susdle-mystery-flip-grid"
      data-mode={mode}
      data-fill={fill ? 'yes' : 'no'}
      aria-label={ariaLabel}
    >
      {order.map((seed) => {
        const cellIndex = Number(seed.slice('cell-'.length));
        return (
          <motion.li
            key={seed}
            layout
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          >
            <span className="susdle-mystery-flip-grid__cell" aria-hidden>
              <span className="susdle-mystery-flip-grid__card">
                <span className="susdle-mystery-flip-grid__card-inner">
                  <span className="susdle-mystery-flip-grid__face susdle-mystery-flip-grid__face--front">
                    <MysteryFrame tone={toneAt(cellIndex)} seed={seed} />
                  </span>
                </span>
              </span>
            </span>
          </motion.li>
        );
      })}
    </ul>
  );
}

function RevealingGrid({
  players,
  awayPlayerIds,
  revealed,
  activePlayerId,
  activeSpeakerLabel,
  ariaLabel,
  renderCellFooter,
  revealedCount,
  fill,
}: RevealingProps): JSX.Element {
  const cascading = revealedCount === undefined;
  return (
    /* eslint-disable-next-line jsx-a11y/no-redundant-roles -- Safari/VoiceOver strip list semantics from <ul> when list-style:none is applied (see MysteryFlipGrid.css); explicit role="list" restores them. */
    <ul
      className="susdle-mystery-flip-grid"
      data-mode="revealing"
      data-cascade={cascading ? 'yes' : 'no'}
      data-fill={fill ? 'yes' : 'no'}
      role="list"
      aria-label={ariaLabel}
    >
      {players.map((player, idx) => {
        const cellRevealed = cascading ? revealed : idx < (revealedCount ?? 0);
        const isActiveSpeaker = revealed && player.id === activePlayerId;
        return (
          <li key={player.id}>
            <span
              /* Dual-class: the new shared class drives the grid + flip
                 CSS; the legacy `susdle-cluegiving-cast-cell` keeps the
                 existing CastGrid.css rules (active-speaker lift,
                 column-flex layout, mobile sizing) working unchanged. */
              className="susdle-mystery-flip-grid__cell susdle-cluegiving-cast-cell"
              data-cell-revealed={cellRevealed ? 'yes' : 'no'}
              data-active-speaker={isActiveSpeaker ? 'yes' : 'no'}
              aria-label={
                isActiveSpeaker && activeSpeakerLabel
                  ? activeSpeakerLabel(player)
                  : undefined
              }
            >
              <span className="susdle-mystery-flip-grid__card">
                <span className="susdle-mystery-flip-grid__card-inner">
                  <span
                    aria-hidden
                    className="susdle-mystery-flip-grid__face susdle-mystery-flip-grid__face--front"
                  >
                    <MysteryFrame tone={toneAt(idx)} seed={player.id} />
                  </span>
                  <span className="susdle-mystery-flip-grid__face susdle-mystery-flip-grid__face--back">
                    <AvatarFrame
                      player={player}
                      isHost={false}
                      isFellowGhost={false}
                      away={awayPlayerIds?.has(player.id) ?? false}
                      motionEnabled={false}
                    />
                  </span>
                </span>
              </span>
              {renderCellFooter ? renderCellFooter(player, idx) : null}
            </span>
          </li>
        );
      })}
    </ul>
  );
}
